import { last } from 'lodash';
import { useTranslation } from 'react-i18next';

interface User {
  id?: number;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
}

export const getFullName = (
  { firstName, lastName, preferredName }: User,
  allowPreferredName = false
) => {
  if (allowPreferredName && preferredName) {
    return preferredName.trim();
  }

  return `${firstName || ''} ${lastName || ''}`.trim();
};

const getFirstName = (
  { firstName, preferredName }: User,
  allowPreferredName: boolean
) => {
  if (allowPreferredName && preferredName) {
    return preferredName.trim();
  }

  return firstName?.trim() ?? '';
};

export const useNameList = (
  users?: User[],
  meId?: number,
  allowPreferredName = false
) => {
  const { t } = useTranslation();
  const hasMe = meId && users?.some((user) => user.id === meId);

  if (!users) {
    return '';
  }

  if (hasMe) {
    if (users.length === 1) {
      return t('words.You');
    }

    const otherUsers = users.filter((user) => user.id !== meId);
    if (users.length === 2) {
      return `${t('words.You')} ${t('words.and')} ${getFullName(otherUsers[0], allowPreferredName)}`;
    }

    const firstNames = [
      t('words.You'),
      ...otherUsers.map((name) => getFirstName(name, allowPreferredName)),
    ];
    return `${firstNames.slice(0, -1).join(', ')} ${t('words.and')} ${last(firstNames)}`;
  }

  if (users.length === 1) {
    return users[0].id === meId
      ? t('words.You')
      : getFullName(users[0], allowPreferredName);
  }

  if (users.length === 2) {
    return `${getFullName(users[0], allowPreferredName)} ${t('words.and')} ${getFullName(users[1], allowPreferredName)}`;
  }

  const firstNames = users.map((name) =>
    getFirstName(name, allowPreferredName)
  );
  return `${firstNames.slice(0, -1).join(', ')} ${t('words.and')} ${last(firstNames)}`;
};
